import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { Compass, Loader2, Plus, Trash2, Users } from "lucide-react";
import { LeafletMap, type MapDepot, type MapPin as MP, type MapStop } from "@/components/maps/LeafletMap";
import { AddressAutocomplete } from "@/components/maps/AddressAutocomplete";
import { listDepots, createDepot, deleteDepot, type Depot } from "@/services/depots";
import { listMappableLeads, createRoute, createSplitRoutes, type MappableLead, type SplitRoutePayload } from "@/services/routes";
import { listTeamMembers, type TeamMember } from "@/services/team";
import { optimizeRoute, formatMiles, formatDuration } from "@/lib/distance";
import { optimizeDriveRoute } from "@/services/directions";
import { splitTerritory, type SplitMode } from "@/lib/territorySplit";
import { useAuthStore } from "@/store/auth";
import { nextSelectionFromClick, pageSelectionState } from "@/lib/selection";

const DIRECTIONS_WAYPOINT_LIMIT = 25;

const MAX_STOPS_OPTIONS = [10, 15, 20, 25, 30, 40, 50, 75, 100] as const;
const PICKER_PAGE_SIZE = 25;
type EndAddressMode = "same_as_start" | "different_saved_address" | "last_stop";

type PlannerPrefs = {
  routeCount: number;
  assigneeMode: "individual" | "pool";
  assignedTo: string;
  selectedIds: string[];
  endMode: EndAddressMode;
  endDepotId: string;
};

function readPlannerPrefs(storageKey: string): PlannerPrefs | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PlannerPrefs>;
    const routeCount = Number(parsed.routeCount);
    const assigneeMode = parsed.assigneeMode === "pool" ? "pool" : "individual";
    return {
      routeCount: Number.isFinite(routeCount) ? routeCount : 20,
      assigneeMode,
      assignedTo: typeof parsed.assignedTo === "string" ? parsed.assignedTo : "",
      selectedIds: Array.isArray(parsed.selectedIds) ? parsed.selectedIds.filter((id): id is string => typeof id === "string") : [],
      endMode: parsed.endMode === "different_saved_address" || parsed.endMode === "last_stop" ? parsed.endMode : "same_as_start",
      endDepotId: typeof parsed.endDepotId === "string" ? parsed.endDepotId : "",
    };
  } catch {
    return null;
  }
}

export function RoutesPlan() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  // Incoming pre-selection from Leads list ("Plan route from N selected").
  // Parsed once on first render; the URL param is then stripped so a reload
  // doesn't re-seed and stomp on changes the user made in the planner.
  const incomingIdsRef = useRef<string[] | null>(null);
  if (incomingIdsRef.current === null) {
    const raw = searchParams.get("leadIds");
    incomingIdsRef.current = raw ? raw.split(",").filter(Boolean) : [];
  }
  const incomingIds = incomingIdsRef.current;

  const depotsQuery = useQuery({ queryKey: ["depots"], queryFn: listDepots });
  // Shared cache key with Routes/Map — any invalidateQueries({ queryKey: ["leads"] })
  // (import, geocode, soft-delete, stop outcome) refreshes the planner automatically.
  const leadsQuery = useQuery({ queryKey: ["leads", "mappable"], queryFn: () => listMappableLeads() });
  const membersQuery = useQuery({ queryKey: ["team", "members"], queryFn: listTeamMembers });

  const depots = useMemo(() => depotsQuery.data ?? [], [depotsQuery.data]);
  const leads = useMemo(() => leadsQuery.data ?? [], [leadsQuery.data]);
  const members = useMemo(() => membersQuery.data ?? [], [membersQuery.data]);
  const plannerStorageKey = useMemo(
    () => `myroute:routes:planner:v4:${me?.teamId ?? "anon"}`,
    [me?.teamId],
  );
  const storedPrefs = useMemo(() => readPlannerPrefs(plannerStorageKey), [plannerStorageKey]);

  const [name, setName] = useState(`Route ${format(new Date(), "MMM d")}`);
  const [routeDate, setRouteDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [depotId, setDepotId] = useState<string>("");
  const [assigneeMode, setAssigneeMode] = useState<"individual" | "pool">(storedPrefs?.assigneeMode ?? "individual");
  const [assignedTo, setAssignedTo] = useState<string>(storedPrefs?.assignedTo ?? "");
  const [endMode, setEndMode] = useState<EndAddressMode>(storedPrefs?.endMode ?? "same_as_start");
  const [endDepotId, setEndDepotId] = useState<string>(storedPrefs?.endDepotId ?? "");
  const [zipFilter, setZipFilter] = useState("");
  const [routeCount, setRouteCount] = useState<number>(storedPrefs?.routeCount ?? 20);
  const [pickerPage, setPickerPage] = useState(1);
  const initialSelectedIds = incomingIds.length > 0 ? incomingIds : (storedPrefs?.selectedIds ?? []);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const lastClickedPickerIdRef = useRef<string | null>(null);
  const [depotModalOpen, setDepotModalOpen] = useState(false);

  useEffect(() => {
    if (!storedPrefs) return;
    setAssigneeMode(storedPrefs.assigneeMode);
    setAssignedTo(storedPrefs.assignedTo);
    setEndMode(storedPrefs.endMode);
    setEndDepotId(storedPrefs.endDepotId);
    // Keep ZIP filter non-sticky to avoid trapping users in an empty route pool.
    setZipFilter("");
    setRouteCount(storedPrefs.routeCount);
    if (incomingIds.length === 0) {
      setSelectedIds(new Set(storedPrefs.selectedIds));
    }
  }, [plannerStorageKey, storedPrefs, incomingIds]);

  // Strip ?leadIds= after consumption so a refresh doesn't keep re-seeding.
  useEffect(() => {
    if (incomingIds.length === 0) return;
    if (!searchParams.get("leadIds")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("leadIds");
    setSearchParams(next, { replace: true });
  }, [incomingIds.length, searchParams, setSearchParams]);

  // Default depot selection — must run as effect, not during render.
  useEffect(() => {
    if (depotId || depots.length === 0) return;
    const def = depots.find((d) => d.is_default) ?? depots[0];
    if (def) setDepotId(def.id);
  }, [depotId, depots]);

  useEffect(() => {
    if (endDepotId || depots.length === 0) return;
    const def = depots.find((d) => d.is_default) ?? depots[0];
    if (def) setEndDepotId(def.id);
  }, [endDepotId, depots]);

  const depot = depots.find((d) => d.id === depotId) ?? null;
  const depotPoint = useMemo(
    () => (depot && depot.lat != null && depot.lng != null ? { lat: depot.lat, lng: depot.lng } : null),
    [depot],
  );
  const endDepot = depots.find((d) => d.id === endDepotId) ?? null;
  const endDepotPoint = useMemo(
    () => (endDepot && endDepot.lat != null && endDepot.lng != null ? { lat: endDepot.lat, lng: endDepot.lng } : null),
    [endDepot],
  );

  useEffect(() => {
    if (endMode !== "different_saved_address") return;
    if (depots.length === 0) return;
    const exists = depots.some((d) => d.id === endDepotId);
    if (exists) return;
    const fallback = depots.find((d) => d.id !== depotId) ?? depots[0];
    setEndDepotId(fallback.id);
  }, [endMode, depots, endDepotId, depotId]);

  useEffect(() => {
    if (!members.some((m) => m.id === assignedTo)) {
      setAssignedTo((prev) => (prev ? "" : prev));
    }
  }, [members, assignedTo]);

  // Prune selected IDs that no longer exist in the lead pool. Gated on
  // `leadsQuery.isSuccess` so the empty initial state doesn't stomp on
  // persisted selection or `?leadIds=` handoffs before the fetch resolves.
  useEffect(() => {
    if (!leadsQuery.isSuccess) return;
    const idSet = new Set(leads.map((l) => l.id));
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (idSet.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [leads, leadsQuery.isSuccess]);

  // One-time handoff diagnostics: if the URL handed off N leads and the
  // planner can only see M < N (the rest aren't geocoded), surface the gap.
  const warnedRef = useRef(false);
  useEffect(() => {
    if (warnedRef.current) return;
    if (!leadsQuery.isSuccess) return;
    if (incomingIds.length === 0) return;
    const loaded = new Set(leads.map((l) => l.id));
    const dropped = incomingIds.filter((id) => !loaded.has(id));
    warnedRef.current = true;
    if (dropped.length > 0) {
      toast(
        `${dropped.length} of ${incomingIds.length} lead${incomingIds.length === 1 ? "" : "s"} couldn't be added. They may not be geocoded, may be marked closed/DNC/follow-up, or may already be on another active route.`,
        { icon: "ℹ️", duration: 7000 },
      );
    }
  }, [leadsQuery.isSuccess, leads, incomingIds]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload: PlannerPrefs = {
      routeCount,
      assigneeMode,
      assignedTo,
      selectedIds: [...selectedIds],
      endMode,
      endDepotId,
    };
    window.localStorage.setItem(plannerStorageKey, JSON.stringify(payload));
  }, [plannerStorageKey, routeCount, assigneeMode, assignedTo, selectedIds, endMode, endDepotId]);

  // Picker scope. Two rules layered on top of the eligible-leads pool:
  //
  //   1. Driver scoping (only when assigneeMode === "individual" + a driver is
  //      chosen): show unassigned leads PLUS leads already owned by this
  //      driver. We deliberately *exclude* leads owned by OTHER drivers — that
  //      keeps a planner from accidentally hijacking another rep's lead. CSV-
  //      imported leads default to assigned_to = null, so the common case is
  //      "all 30 leads still visible" rather than "0 matching" (the previous
  //      strict-equality filter would hide every freshly imported lead).
  //
  //   2. ZIP starts-with filter (always honored, both modes).
  const filteredLeads = useMemo(() => {
    let scoped = leads;
    if (assigneeMode === "individual" && assignedTo) {
      scoped = scoped.filter((l) => !l.assigned_to || l.assigned_to === assignedTo);
    }
    const z = zipFilter.trim();
    if (!z) return scoped;
    return scoped.filter((l) => l.address_zip?.startsWith(z));
  }, [leads, assigneeMode, assignedTo, zipFilter]);

  // Diagnostic for the picker empty-state. Tells the user *why* zero leads
  // match instead of the generic "check ZIP or geocoding" hint, which sent
  // them on a wild goose chase when the real reason was the driver filter.
  const emptyPickerReason = useMemo(() => {
    if (filteredLeads.length > 0) return null;
    if (leads.length === 0) {
      return "No eligible leads yet. Import a CSV and make sure rows are geocoded.";
    }
    if (zipFilter.trim()) {
      return `ZIP "${zipFilter.trim()}" hides every candidate. Clear it to see all leads.`;
    }
    if (assigneeMode === "individual" && assignedTo) {
      return "Every eligible lead is already owned by a different driver. Switch to Team pool, pick another driver, or set those leads to unassigned in Leads.";
    }
    return "All eligible leads are already on this route — or none are geocoded yet. Open Leads to check.";
  }, [filteredLeads.length, leads.length, zipFilter, assigneeMode, assignedTo]);

  const selectedLeads = useMemo(
    () => leads.filter((l) => selectedIds.has(l.id)),
    [leads, selectedIds],
  );

  const routeEndPoint = useMemo(() => {
    if (!depotPoint) return null;
    if (endMode === "last_stop") return null;
    if (endMode === "same_as_start") return depotPoint;
    return endDepotPoint;
  }, [depotPoint, endMode, endDepotPoint]);

  // Fast crow-flies preview. Drives the picker's "expansion anchor" so the
  // ranked-nearest sort can respond as you tick boxes. The Google Directions
  // call below replaces this for the map + save once it lands (200–400 ms).
  const optimization = useMemo(() => {
    if (!depotPoint || selectedLeads.length === 0) return null;
    return optimizeRoute(depotPoint, selectedLeads, routeEndPoint);
  }, [depotPoint, selectedLeads, routeEndPoint]);

  // Real road-network route. Google does the TSP reorder via optimize:true,
  // respects one-way streets, returns the actual road polyline + per-leg
  // drive distances/durations. Skipped when the selection exceeds Google's
  // 25-waypoint cap (haversine preview is still shown in that case).
  const selectedIdsKey = useMemo(() => {
    return [...selectedIds].sort().join(",");
  }, [selectedIds]);
  const driveDirectionsQuery = useQuery({
    enabled:
      !!depotPoint &&
      selectedLeads.length > 0 &&
      selectedLeads.length <= DIRECTIONS_WAYPOINT_LIMIT,
    queryKey: [
      "route-directions",
      depotPoint?.lat, depotPoint?.lng,
      routeEndPoint?.lat ?? null, routeEndPoint?.lng ?? null,
      selectedIdsKey,
    ],
    queryFn: () =>
      optimizeDriveRoute({
        origin: depotPoint!,
        destination: routeEndPoint,
        waypoints: selectedLeads.map((l) => ({ id: l.id, lat: l.lat, lng: l.lng })),
        optimize: true,
      }),
    // Same coordinates → same drive route; cache for the session.
    staleTime: 30 * 60_000,
    retry: 1,
  });
  const driveResult = driveDirectionsQuery.data ?? null;

  // Compose "effective" route: prefer Google's road geometry + ordering when
  // available, fall back to the haversine preview while the request is in
  // flight or when we exceed the 25-waypoint cap. SAVE uses these too so the
  // stored total_distance_m and per-leg figures are real road miles.
  const leadsById = useMemo(() => {
    const m = new Map<string, typeof selectedLeads[number]>();
    for (const l of selectedLeads) m.set(l.id, l);
    return m;
  }, [selectedLeads]);
  const effective = useMemo(() => {
    if (driveResult) {
      const ordered = driveResult.orderedWaypointIds
        .map((id) => leadsById.get(id))
        .filter((l): l is NonNullable<typeof l> => !!l);
      return {
        ordered,
        legs: driveResult.legs.map((l) => l.distance_m),
        totalMeters: driveResult.total_distance_m,
        totalSeconds: driveResult.total_duration_s,
        polyline: driveResult.polyline,
        source: "google" as const,
      };
    }
    if (!optimization || !depotPoint) return null;
    // Haversine fallback polyline: straight legs depot → stops → end.
    const straightPolyline: Array<[number, number]> = [
      [depotPoint.lat, depotPoint.lng],
      ...optimization.ordered.map((l) => [l.lat, l.lng] as [number, number]),
      ...(routeEndPoint ? [[routeEndPoint.lat, routeEndPoint.lng] as [number, number]] : []),
    ];
    return {
      ordered: optimization.ordered,
      legs: optimization.legs,
      totalMeters: optimization.totalMeters,
      totalSeconds: null as number | null,
      polyline: straightPolyline,
      source: "haversine" as const,
    };
  }, [driveResult, optimization, depotPoint, routeEndPoint, leadsById]);

  const distanceLabel = useMemo(() => {
    if (!effective) return null;
    const miles = formatMiles(effective.totalMeters);
    const duration = effective.totalSeconds != null
      ? ` · ${formatDuration(effective.totalSeconds)} drive`
      : "";
    const tail = effective.source === "haversine" ? " (preview)" : "";
    if (endMode === "last_stop") {
      return `${effective.ordered.length} stops · ${miles}${duration} door-to-door${tail}`;
    }
    if (endMode === "different_saved_address") {
      const endName = endDepot?.name ?? "selected end address";
      return `${effective.ordered.length} stops · ${miles}${duration} (ends at ${endName})${tail}`;
    }
    return `${effective.ordered.length} stops · ${miles}${duration} round-trip${tail}`;
  }, [effective, endMode, endDepot]);

  const expansionAnchor = useMemo(() => {
    // Use the effective (Google when available, haversine otherwise) tail so
    // "show me what's nearby" keeps tracking the real drive end.
    if (effective?.ordered?.length) {
      const tail = effective.ordered[effective.ordered.length - 1];
      return { lat: tail.lat, lng: tail.lng };
    }
    return depotPoint;
  }, [effective, depotPoint]);

  const rankedFilteredLeads = useMemo(() => {
    if (!expansionAnchor) return filteredLeads;
    return [...filteredLeads].sort((a, b) => {
      const da = (a.lat - expansionAnchor.lat) ** 2 + (a.lng - expansionAnchor.lng) ** 2;
      const db = (b.lat - expansionAnchor.lat) ** 2 + (b.lng - expansionAnchor.lng) ** 2;
      return da - db;
    });
  }, [filteredLeads, expansionAnchor]);

  const pickerPageCount = Math.max(1, Math.ceil(rankedFilteredLeads.length / PICKER_PAGE_SIZE));
  const pickerLeads = useMemo(() => {
    const start = (pickerPage - 1) * PICKER_PAGE_SIZE;
    return rankedFilteredLeads.slice(start, start + PICKER_PAGE_SIZE);
  }, [rankedFilteredLeads, pickerPage]);

  useEffect(() => {
    setPickerPage(1);
  }, [zipFilter, assigneeMode, assignedTo]);

  useEffect(() => {
    if (pickerPage > pickerPageCount) {
      setPickerPage(pickerPageCount);
    }
  }, [pickerPage, pickerPageCount]);

  // Picker click handler that supports plain click + shift-click range, capped
  // by routeCount. Range items that would push past the cap are dropped (with
  // a toast) so the user knows the limit was hit.
  function handlePickerClick(id: string, shiftKey: boolean, pageIds: string[]) {
    setSelectedIds((prev) => {
      const candidate = nextSelectionFromClick(id, shiftKey, pageIds, lastClickedPickerIdRef.current, prev);
      if (candidate.size <= routeCount) return candidate;
      // Cap exceeded — preserve prior selection that survived this click, then
      // fill remaining slots from the candidate set in page order so the
      // closest leads get priority.
      const next = new Set<string>();
      prev.forEach((pid) => { if (candidate.has(pid)) next.add(pid); });
      for (const pid of pageIds) {
        if (next.size >= routeCount) break;
        if (candidate.has(pid)) next.add(pid);
      }
      // Fall back to non-page candidate items (shouldn't normally happen for
      // shift-range, but keeps the invariant simple).
      candidate.forEach((cid) => {
        if (next.size >= routeCount) return;
        next.add(cid);
      });
      toast.error(`Max stops is ${routeCount}. Raise it above to add more.`);
      return next;
    });
    lastClickedPickerIdRef.current = id;
  }

  function togglePickerPage(pageIds: string[]) {
    setSelectedIds((prev) => {
      // Any-selected-clears semantics: if the header is checked OR
      // indeterminate, clicking it clears the page. Only an empty page selects.
      const anyOnPage = pageIds.some((pid) => prev.has(pid));
      const next = new Set(prev);
      if (anyOnPage) {
        pageIds.forEach((pid) => next.delete(pid));
        return next;
      }
      let hitCap = false;
      for (const pid of pageIds) {
        if (next.has(pid)) continue;
        if (next.size >= routeCount) { hitCap = true; break; }
        next.add(pid);
      }
      if (hitCap) toast.error(`Max stops is ${routeCount}. Raise it above to add more.`);
      return next;
    });
    lastClickedPickerIdRef.current = null;
  }

  function autoPickNearest() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const l of rankedFilteredLeads) {
        if (next.size >= routeCount) break;
        next.add(l.id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
    lastClickedPickerIdRef.current = null;
  }

  // ---- Territory splitter (compass / zip → multi-driver) ----
  // The splitter reuses the existing lead selection + depot + end choice
  // already on the Plan page. The user picks N drivers + a mode in a modal
  // and the system runs splitTerritory + atomically creates K routes via
  // the create_split_routes RPC. Forward-thinking: same lib supports more
  // grouping modes later (city, state, route template) by adding a third
  // case to splitTerritory's switch.
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitMode, setSplitMode] = useState<SplitMode>("compass");
  const [splitDriverIds, setSplitDriverIds] = useState<Set<string>>(new Set());

  const splitMutation = useMutation({
    mutationFn: async () => {
      if (!me) throw new Error("Not signed in");
      if (!depot || !depotPoint) throw new Error("Pick a start address first");
      if (selectedLeads.length === 0) throw new Error("Pick at least one lead");
      const driverList = (membersQuery.data ?? [])
        .filter((m) => m.isActive && splitDriverIds.has(m.id))
        .map((m) => ({ id: m.id, name: m.fullName || m.email }));
      if (driverList.length < 2) throw new Error("Pick at least 2 drivers to split among");

      const split = splitTerritory({
        mode: splitMode,
        origin: depotPoint,
        end: routeEndPoint,
        leads: selectedLeads,
        drivers: driverList,
      });

      // Build per-driver SplitRoutePayload from the splitter's optimized
      // orderings. Empty assignments (a driver who got buckets with no
      // routable leads) are filtered out — no point creating a 0-stop route.
      const today = routeDate || format(new Date(), "yyyy-MM-dd");
      const payloads: SplitRoutePayload[] = [];
      for (const a of split.assignments) {
        if (a.orderedLeadIds.length === 0) continue;
        const leadById = new Map(a.leads.map((l) => [l.id, l]));
        const stops = a.orderedLeadIds.map((id, idx) => {
          // distance_from_prev_m is 0 here (haversine sequence not propagated
          // back from splitTerritory at the leg level; only totalMeters). The
          // Detail page will reconcile real per-leg distances on first view
          // via the Google Routes API call when polyline_encoded is null.
          void leadById; // tag to keep the map alive for future improvements
          return { lead_id: id, sequence: idx + 1, distance_from_prev_m: 0 };
        });
        payloads.push({
          name: `${name || "Dispatch"} — ${a.driverName} (${a.groupLabels.join(",") || "—"})`,
          route_date: today,
          depot_start_id: depot.id,
          depot_end_id: endMode === "last_stop" ? null : endMode === "same_as_start" ? depot.id : (endDepot?.id ?? null),
          assigned_to: a.driverId,
          is_pool: false,
          total_distance_m: Math.round(a.totalMeters),
          stops,
        });
      }
      if (payloads.length === 0) {
        throw new Error("No routable leads landed in any driver's slice. Geocode leads or pick different drivers.");
      }
      const ids = await createSplitRoutes(payloads);
      return { ids, payloads, unassignedCount: split.unassigned.length };
    },
    onSuccess: ({ ids, unassignedCount }) => {
      const msg = unassignedCount > 0
        ? `Dispatched ${ids.length} routes. ${unassignedCount} leads excluded (no address/coords).`
        : `Dispatched ${ids.length} routes`;
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["routes"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      setSplitOpen(false);
      navigate("/routes");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!me) throw new Error("Not signed in");
      if (!depot) throw new Error("Pick a depot");
      if (endMode === "different_saved_address" && !endDepot) throw new Error("Pick an end address");
      if (!effective || effective.ordered.length === 0) throw new Error("Pick at least one lead");
      const stops = effective.ordered.map((lead, idx) => ({
        lead_id: lead.id,
        sequence: idx + 1,
        distance_from_prev_m: Math.round(effective.legs[idx] ?? 0),
      }));
      const id = await createRoute(
        {
          name,
          route_date: routeDate,
          depot_start_id: depot.id,
          depot_end_id: endMode === "last_stop" ? null : endMode === "same_as_start" ? depot.id : endDepot!.id,
          assigned_to: assigneeMode === "individual" ? (assignedTo || null) : null,
          is_pool: assigneeMode === "pool",
          total_distance_m: Math.round(effective.totalMeters),
          // Cache the live drive-time + Google polyline so Detail.tsx hydrates
          // from DB instead of re-calling Routes API on every page view. Only
          // set when the live Routes API call succeeded (source === "google");
          // haversine-only saves leave the cache null and Detail falls back to
          // a live lookup on first render.
          total_duration_s: effective.source === "google" && effective.totalSeconds != null
            ? Math.round(effective.totalSeconds)
            : undefined,
          polyline_encoded: effective.source === "google" ? driveResult?.polyline_encoded ?? null : null,
          stops,
        },
        { teamId: me.teamId, userId: me.id },
      );
      return id;
    },
    onSuccess: (id) => {
      toast.success("Route created");
      qc.invalidateQueries({ queryKey: ["routes"] });
      // Saved stops now block these leads from being added to another route —
      // refresh the mappable pool everywhere it's observed.
      qc.invalidateQueries({ queryKey: ["leads"] });
      navigate(`/routes/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Build map data
  const mapDepots: MapDepot[] = [];
  if (depot && depotPoint) {
    mapDepots.push({ id: depot.id, lat: depotPoint.lat, lng: depotPoint.lng, label: `Start: ${depot.name}` });
  }
  if (
    endMode === "different_saved_address"
    && endDepot
    && endDepotPoint
    && endDepot.id !== depot?.id
  ) {
    mapDepots.push({ id: endDepot.id, lat: endDepotPoint.lat, lng: endDepotPoint.lng, label: `End: ${endDepot.name}` });
  }

  const mapStops: MapStop[] = effective
    ? effective.ordered.map((l, i) => ({
        id: l.id,
        lat: l.lat,
        lng: l.lng,
        sequence: i + 1,
        title: leadName(l),
        subtitle: leadAddr(l),
      }))
    : [];

  // Background pins = unselected & filtered
  const mapBackground: MP[] = filteredLeads
    .filter((l) => !selectedIds.has(l.id))
    .map((l) => ({ id: l.id, lat: l.lat, lng: l.lng, title: leadName(l), subtitle: leadAddr(l) }));

  // Polyline preference: real road geometry from Google when available
  // (curves around lakes, follows highways), straight-line preview otherwise.
  const polyline: Array<[number, number]> | undefined = effective?.polyline;

  const loading = depotsQuery.isLoading || leadsQuery.isLoading;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_1fr]">
      {/* ---- Left rail: settings + lead picker ----------------------------- */}
      <div className="space-y-4">
        <Card title="Route">
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </Field>
          <Field label="Date">
            <input
              type="date"
              value={routeDate}
              onChange={(e) => setRouteDate(e.target.value)}
              className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            />
          </Field>
          <Field label="Assignee">
            <div className="grid grid-cols-2 gap-1 rounded-md bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setAssigneeMode("individual")}
                className={`rounded px-2 py-1 text-xs font-medium transition ${
                  assigneeMode === "individual"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Specific driver
              </button>
              <button
                type="button"
                onClick={() => setAssigneeMode("pool")}
                className={`rounded px-2 py-1 text-xs font-medium transition ${
                  assigneeMode === "pool"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Team pool
              </button>
            </div>
          </Field>
          {assigneeMode === "individual" ? (
            <Field label="Driver">
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">— unassigned —</option>
                {members
                  .filter((m: TeamMember) => m.isActive)
                  .map((m) => {
                    const suffix = m.role === "admin_user" ? "(admin)" : m.role === "route_manager" ? "(route mgr)" : "";
                    return (
                      <option key={m.id} value={m.id}>
                        {m.fullName} {suffix}
                      </option>
                    );
                  })}
              </select>
            </Field>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Any active driver in your team will see this route and can claim it. First to claim, owns it.
            </p>
          )}
        </Card>

        <Card
          title="Start address"
          actions={
            <button
              type="button"
              onClick={() => setDepotModalOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
            >
              <Plus className="h-3 w-3" /> Manage
            </button>
          }
        >
          {depots.length === 0 ? (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">No start address yet.</p>
              <p className="mt-0.5">Click <strong>Manage</strong> to save your office or dispatch location first.</p>
            </div>
          ) : (
            <>
              <select
                value={depotId}
                onChange={(e) => setDepotId(e.target.value)}
                className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                {depots.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.is_default ? " (default)" : ""}
                  </option>
                ))}
              </select>
              {depot && (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Confirmed start</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{depot.name}</p>
                  <p className="text-xs text-slate-600">{depotAddress(depot)}</p>
                </div>
              )}
            </>
          )}
        </Card>

        <Card title="End address">
          {depots.length === 0 ? (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Add a start address first, then choose how this route should end.
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <input
                    type="radio"
                    name="end-mode"
                    checked={endMode === "same_as_start"}
                    onChange={() => setEndMode("same_as_start")}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="block font-medium text-slate-900">Same as start</span>
                    <span className="block text-xs text-slate-500">Round-trip route back to your start address.</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <input
                    type="radio"
                    name="end-mode"
                    checked={endMode === "different_saved_address"}
                    onChange={() => {
                      setEndMode("different_saved_address");
                      if (!endDepotId) {
                        const fallback = depots.find((d) => d.id !== depotId) ?? depots[0];
                        if (fallback) setEndDepotId(fallback.id);
                      }
                    }}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="block font-medium text-slate-900">Different saved address</span>
                    <span className="block text-xs text-slate-500">End somewhere else, like home or another office.</span>
                  </span>
                </label>
                {endMode === "different_saved_address" && (
                  <select
                    value={endDepotId}
                    onChange={(e) => setEndDepotId(e.target.value)}
                    className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  >
                    <option value="" disabled>Select end address</option>
                    {depots.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                        {d.id === depotId ? " (same as start)" : ""}
                      </option>
                    ))}
                  </select>
                )}
                <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <input
                    type="radio"
                    name="end-mode"
                    checked={endMode === "last_stop"}
                    onChange={() => setEndMode("last_stop")}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span>
                    <span className="block font-medium text-slate-900">End at last stop (no return)</span>
                    <span className="block text-xs text-slate-500">Best for long routes when your final destination is unknown.</span>
                  </span>
                </label>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Confirmed end</p>
                {endMode === "same_as_start" && depot && (
                  <>
                    <p className="mt-1 text-sm font-medium text-slate-900">{depot.name}</p>
                    <p className="text-xs text-slate-600">{depotAddress(depot)}</p>
                  </>
                )}
                {endMode === "different_saved_address" && endDepot && (
                  <>
                    <p className="mt-1 text-sm font-medium text-slate-900">{endDepot.name}</p>
                    <p className="text-xs text-slate-600">{depotAddress(endDepot)}</p>
                  </>
                )}
                {endMode === "different_saved_address" && !endDepot && (
                  <p className="mt-1 text-xs text-amber-700">Select a saved end address.</p>
                )}
                {endMode === "last_stop" && (
                  <p className="mt-1 text-xs text-slate-700">Route ends at the final selected lead; no return leg is added.</p>
                )}
              </div>
            </>
          )}
        </Card>

        <Card title="Pick leads">
          <Field label="ZIP starts with (optional)">
            <div className="flex gap-2">
              <input
                type="text"
                value={zipFilter}
                onChange={(e) => setZipFilter(e.target.value)}
                placeholder="e.g. 482"
                className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
              />
              {zipFilter.trim() && (
                <button
                  type="button"
                  onClick={() => setZipFilter("")}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Clear ZIP
                </button>
              )}
            </div>
          </Field>
          <Field label="Max stops">
            <select
              value={String(routeCount)}
              onChange={(e) => setRouteCount(Number(e.target.value))}
              className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
            >
              {MAX_STOPS_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} stops</option>
              ))}
            </select>
          </Field>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={autoPickNearest}
              disabled={rankedFilteredLeads.length === 0 || selectedIds.size >= routeCount}
              className="flex-1 rounded-md bg-brand-600 px-2 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Auto-pick nearest {routeCount}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={selectedIds.size === 0}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              Clear
            </button>
          </div>
          {emptyPickerReason && (
            <p className="text-[11px] text-amber-700">{emptyPickerReason}</p>
          )}
          <p className="mt-2 text-[11px] text-slate-500">
            <span className="font-semibold text-slate-700">{selectedIds.size}</span> of {routeCount} selected · {filteredLeads.length.toLocaleString()} matching the filter · {leads.length.toLocaleString()} eligible total
          </p>
        </Card>

        <SaveRoutePanel
          onSplit={() => setSplitOpen(true)}
          splitDisabled={!depotPoint || selectedLeads.length === 0}
          hasSelection={!!effective && effective.ordered.length > 0}
          distanceLabel={distanceLabel}
          depotMissing={!depot}
          endAddressMissing={endMode === "different_saved_address" && !endDepot}
          noLeads={leadsQuery.isSuccess && leads.length === 0}
          nothingSelected={selectedIds.size === 0}
          saving={saveMutation.isPending}
          onSave={() => saveMutation.mutate()}
        />
      </div>

      {/* ---- Right: map + selected list ------------------------------------ */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            <LeafletMap
              leads={mapBackground}
              depots={mapDepots}
              stops={mapStops}
              polyline={polyline}
              height={520}
            />
            <SelectedTable
              orderedLeads={effective?.ordered ?? selectedLeads}
              orderSource={effective?.source ?? "haversine"}
              onRemove={(id) =>
                setSelectedIds((p) => {
                  const n = new Set(p);
                  n.delete(id);
                  return n;
                })
              }
              onClearAll={clearSelection}
            />
            {/* Candidate leads, sorted nearest-first to the depot (or last selected stop) */}
            <PickerTable
              leads={pickerLeads}
              selectedIds={selectedIds}
              onPickerClick={handlePickerClick}
              onTogglePage={togglePickerPage}
              totalFiltered={filteredLeads.length}
              emptyReason={emptyPickerReason}
              zipFilter={zipFilter}
              onClearZip={() => setZipFilter("")}
              onOpenLeads={() => navigate("/leads")}
              showSwitchToPool={assigneeMode === "individual" && !!assignedTo && leads.length > 0 && !zipFilter.trim()}
              onSwitchToPool={() => setAssigneeMode("pool")}
              page={pickerPage}
              pageCount={pickerPageCount}
              pageSize={PICKER_PAGE_SIZE}
              onPrevPage={() => setPickerPage((p) => Math.max(1, p - 1))}
              onNextPage={() => setPickerPage((p) => Math.min(pickerPageCount, p + 1))}
            />
          </>
        )}
      </div>

      <DepotsDialog
        open={depotModalOpen}
        onClose={() => setDepotModalOpen(false)}
        depots={depots}
        teamId={me?.teamId ?? ""}
        onChanged={() => qc.invalidateQueries({ queryKey: ["depots"] })}
      />

      <SplitterDialog
        open={splitOpen}
        onClose={() => setSplitOpen(false)}
        mode={splitMode}
        setMode={setSplitMode}
        driverIds={splitDriverIds}
        setDriverIds={setSplitDriverIds}
        activeMembers={(membersQuery.data ?? []).filter((m) => m.isActive)}
        leads={selectedLeads}
        depotPoint={depotPoint}
        endPoint={routeEndPoint}
        onConfirm={() => splitMutation.mutate()}
        running={splitMutation.isPending}
      />
    </div>
  );
}

// ============================================================================
//  Sub-components
// ============================================================================

function SaveRoutePanel({
  hasSelection,
  distanceLabel,
  depotMissing,
  endAddressMissing,
  noLeads,
  nothingSelected,
  saving,
  onSave,
  onSplit,
  splitDisabled,
}: {
  hasSelection: boolean;
  distanceLabel: string | null;
  depotMissing: boolean;
  endAddressMissing: boolean;
  noLeads: boolean;
  nothingSelected: boolean;
  saving: boolean;
  onSave: () => void;
  onSplit: () => void;
  splitDisabled: boolean;
}) {
  // One reason at a time, in the order the user needs to resolve them.
  let blockReason: string | null = null;
  if (noLeads) blockReason = "No eligible leads right now. Leads must be geocoded, not closed/DNC/follow-up, and not already pending on an active route.";
  else if (depotMissing) blockReason = "Add a start address — click Manage in the card above.";
  else if (endAddressMissing) blockReason = "Pick an end address, or choose Same as start / End at last stop.";
  else if (nothingSelected) blockReason = "Pick at least one lead from the list below — or use Auto-pick nearest.";

  const canSave = !blockReason && hasSelection;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Save route</h3>
      {hasSelection && distanceLabel && (
        <p className="mt-2 text-sm text-slate-700">
          <span className="font-semibold">{distanceLabel}</span>
        </p>
      )}
      {blockReason && (
        <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
          {blockReason}
        </p>
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={!canSave || saving}
        className="mt-3 w-full rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="h-4 w-4 animate-spin" /> Saving…
          </span>
        ) : (
          "Save route"
        )}
      </button>
      {/* Dispatch to multiple drivers — opens the territory splitter modal.
          Compass (8 sectors from depot) or ZIP (one bucket per ZIP). Stays
          tucked under the primary Save button so the single-driver flow
          remains the default path. */}
      <button
        type="button"
        onClick={onSplit}
        disabled={splitDisabled || saving}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        title="Split the selected leads across multiple drivers by compass sector or ZIP"
      >
        <Users className="h-3.5 w-3.5" />
        Split across drivers…
      </button>
    </section>
  );
}

function Card({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        {actions}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function leadName(l: MappableLead): string {
  return (l.full_name ?? `${l.first_name ?? ""} ${l.last_name ?? ""}`.trim()) || "Lead";
}
function leadAddr(l: MappableLead): string {
  return [l.address_street, l.address_city, l.address_state, l.address_zip].filter(Boolean).join(", ");
}

function depotAddress(d: Depot): string {
  return [d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(", ") || "Address details unavailable";
}

function SelectedTable({
  orderedLeads,
  orderSource,
  onRemove,
  onClearAll,
}: {
  orderedLeads: MappableLead[];
  orderSource: "google" | "haversine";
  onRemove: (id: string) => void;
  onClearAll: () => void;
}) {
  if (orderedLeads.length === 0) return null;
  // Voice: agents think in visits, not "orders" (which collides with policy
  // orders in life-insurance speak). "Sequence" sidesteps the noun overload.
  const orderLabel = orderSource === "google"
    ? "optimized visit sequence"
    : "preview — calculating drive…";
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Selected stops ({orderedLeads.length}) <span className="font-normal normal-case text-slate-400">· {orderLabel}</span>
        </h3>
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-0.5 text-[11px] font-medium text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="h-3 w-3" /> Clear all
        </button>
      </header>
      <table className="min-w-full divide-y divide-slate-100">
        <tbody className="divide-y divide-slate-100">
          {orderedLeads.map((l, i) => (
            <tr key={l.id} className="hover:bg-slate-50">
              <td className="w-8 px-3 py-2 text-xs font-bold text-sky-700">{i + 1}</td>
              <td className="px-3 py-2">
                <div className="text-sm font-medium text-slate-900">{leadName(l)}</div>
                <div className="text-xs text-slate-500">{leadAddr(l)}</div>
              </td>
              <td className="w-8 px-3 py-2 text-right">
                <button type="button" onClick={() => onRemove(l.id)} className="text-slate-400 hover:text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PickerTable({
  leads,
  selectedIds,
  onPickerClick,
  onTogglePage,
  totalFiltered,
  emptyReason,
  zipFilter,
  onClearZip,
  onOpenLeads,
  showSwitchToPool,
  onSwitchToPool,
  page,
  pageCount,
  pageSize,
  onPrevPage,
  onNextPage,
}: {
  leads: MappableLead[];
  selectedIds: Set<string>;
  onPickerClick: (id: string, shiftKey: boolean, pageIds: string[]) => void;
  onTogglePage: (pageIds: string[]) => void;
  totalFiltered: number;
  emptyReason: string | null;
  zipFilter: string;
  onClearZip: () => void;
  onOpenLeads: () => void;
  showSwitchToPool: boolean;
  onSwitchToPool: () => void;
  page: number;
  pageCount: number;
  pageSize: number;
  onPrevPage: () => void;
  onNextPage: () => void;
}) {
  if (leads.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-xs text-slate-500">
        {totalFiltered === 0 ? (
          <div className="space-y-2">
            <p className="text-slate-700">{emptyReason ?? "No leads available for this planner view."}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {zipFilter.trim() && (
                <button
                  type="button"
                  onClick={onClearZip}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Clear ZIP filter
                </button>
              )}
              {showSwitchToPool && (
                <button
                  type="button"
                  onClick={onSwitchToPool}
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
                >
                  Switch to Team pool
                </button>
              )}
              {!zipFilter.trim() && !showSwitchToPool && (
                <button
                  type="button"
                  onClick={onOpenLeads}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Open Leads
                </button>
              )}
            </div>
          </div>
        ) : (
          "No leads match the current filter."
        )}
      </div>
    );
  }

  const pageStart = (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, totalFiltered);
  const pageIds = leads.map((l) => l.id);
  const pageState = pageSelectionState(pageIds, selectedIds);
  const selectedOnPage = leads.reduce((n, l) => (selectedIds.has(l.id) ? n + 1 : n), 0);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <input
            type="checkbox"
            checked={pageState === "all"}
            ref={(el) => {
              if (el) el.indeterminate = pageState === "some";
            }}
            onChange={() => onTogglePage(pageIds)}
            aria-label={pageState === "none" ? "Select all leads on this page" : "Clear selection on this page"}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            Available leads ({pageStart}–{pageEnd} of {totalFiltered})
            {selectedOnPage > 0 && (
              <span className="ml-1 normal-case text-[10px] font-medium text-brand-700">
                · {selectedOnPage} on this page
              </span>
            )}
          </span>
        </label>
        {pageCount > 1 && (
          <div className="flex items-center gap-2 text-[11px] text-slate-600">
            <button type="button" onClick={onPrevPage} disabled={page <= 1} className="rounded border border-slate-300 px-2 py-0.5 disabled:opacity-40">Prev</button>
            <span>Page {page} / {pageCount}</span>
            <button type="button" onClick={onNextPage} disabled={page >= pageCount} className="rounded border border-slate-300 px-2 py-0.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </header>
      <table className="min-w-full divide-y divide-slate-100">
        <tbody className="divide-y divide-slate-100">
          {leads.map((l) => {
            const isSelected = selectedIds.has(l.id);
            return (
              <tr
                key={l.id}
                className={`hover:bg-slate-50 ${isSelected ? "bg-sky-50/70" : ""}`}
              >
                <td className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPickerClick(l.id, e.shiftKey, pageIds);
                    }}
                    onChange={() => { /* handled in onClick to capture shiftKey */ }}
                    aria-label={`${isSelected ? "Unselect" : "Select"} ${leadName(l)}`}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                </td>
                <td className="px-3 py-2">
                  <div className={`text-sm font-medium ${isSelected ? "text-brand-800" : "text-slate-900"}`}>{leadName(l)}</div>
                  <div className="text-xs text-slate-500">{leadAddr(l)}</div>
                </td>
                <td className="px-3 py-2 text-right text-xs text-slate-500">{l.address_zip}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// ============================================================================
//  Depot management dialog
// ============================================================================

function DepotsDialog({
  open,
  onClose,
  depots,
  teamId,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  depots: Depot[];
  teamId: string;
  onChanged: () => void;
}) {
  // Two states: "list" shows saved addresses, "form" reveals the new-address form.
  // First-time use (no addresses) jumps straight to the form so there's something
  // to do. After save, we return to "list" so the user sees what they created
  // instead of an empty form pretending another save is required.
  const [view, setView] = useState<"list" | "form">(depots.length === 0 ? "form" : "list");
  const [name, setName] = useState("");
  const [addressQuery, setAddressQuery] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  // Re-evaluate default view each time the dialog opens.
  useEffect(() => {
    if (open) setView(depots.length === 0 ? "form" : "list");
  }, [open, depots.length]);

  function resetForm() {
    setName(""); setAddressQuery(""); setStreet(""); setCity(""); setState(""); setZip(""); setLat(""); setLng(""); setIsDefault(false);
  }

  const createMut = useMutation({
    mutationFn: () =>
      createDepot(
        {
          name,
          address_street: street || undefined,
          address_city: city || undefined,
          address_state: state || undefined,
          address_zip: zip || undefined,
          lat: Number(lat),
          lng: Number(lng),
          is_default: isDefault,
        },
        teamId,
      ),
    onSuccess: () => {
      toast.success("Address saved");
      resetForm();
      onChanged();
      setView("list");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDepot(id),
    onSuccess: () => {
      toast.success("Address removed");
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Give this address a label first");
    if (!lat || !lng) return toast.error("Pick the address from the suggestions so we can geocode it");
    createMut.mutate();
  }

  return (
    <Transition show={open}>
      {/* z-[1100] beats Leaflet's controls (z-index 1000) so the map can't punch through */}
      <Dialog onClose={onClose} className="relative z-[1100]">
        <Transition.Child
          enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
              <Dialog.Title className="text-lg font-semibold text-slate-900">Saved addresses</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-slate-500">
                Save office, dispatch, or home addresses here. You'll choose which one is start and end while planning the route.
              </Dialog.Description>

              {depots.length > 0 && (
                <ul className="mt-4 divide-y divide-slate-100 rounded-md border border-slate-200">
                  {depots.map((d) => (
                    <li key={d.id} className="flex items-center justify-between px-3 py-2">
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {d.name}{" "}
                          {d.is_default && <span className="ml-1 rounded bg-brand-50 px-1 py-0.5 text-[10px] font-medium text-brand-700">DEFAULT</span>}
                        </div>
                        <div className="text-xs text-slate-500">
                          {[d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(", ") || "—"}{" "}
                          <span className="ml-2 font-mono">{d.lat?.toFixed(5)}, {d.lng?.toFixed(5)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteMut.mutate(d.id)}
                        disabled={deleteMut.isPending}
                        className="text-slate-400 hover:text-rose-600 disabled:opacity-40"
                        aria-label="Remove address"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {view === "list" ? (
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("form")}
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add another address
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {depots.length === 0 ? "Add your first address" : "Add another address"}
                  </h4>
                  <Field label="Label (e.g. Main office, North dispatch)">
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Main office"
                      className="w-full rounded-md border-slate-300 px-2 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                    />
                  </Field>
                  <Field label="Address">
                    <AddressAutocomplete
                      value={addressQuery}
                      onChange={(v) => { setAddressQuery(v); if (lat || lng) { setLat(""); setLng(""); } }}
                      onSelect={(addr) => {
                        setStreet(addr.street);
                        setCity(addr.city);
                        setState(addr.state);
                        setZip(addr.zip);
                        setLat(String(addr.lat));
                        setLng(String(addr.lng));
                        setAddressQuery(addr.formatted);
                      }}
                    />
                  </Field>
                  {(street || city || state || zip) && (
                    <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200">
                      <span className="font-medium text-slate-800">Resolved:</span>{" "}
                      {[street, city, state, zip].filter(Boolean).join(", ")}
                      {lat && lng && (
                        <span className="ml-2 font-mono text-slate-400">
                          {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                        </span>
                      )}
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    Use this as the default start address for new routes
                  </label>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        // First-time empty state: close instead of toggling to an empty list.
                        if (depots.length === 0) onClose();
                        else { resetForm(); setView("list"); }
                      }}
                      className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMut.isPending || !lat || !lng}
                      className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {createMut.isPending ? "Adding…" : "Add address"}
                    </button>
                  </div>
                </form>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

// ============================================================================
//  Territory Splitter Dialog
// ============================================================================
// Compass (8 sectors from depot) or ZIP (one bucket per address_zip).
// User picks 2+ active drivers; sectors/ZIPs distribute clockwise/ascending.
// Preview shows the count per bucket and which driver each bucket lands with.
// Confirm fires the atomic create_split_routes RPC.

function SplitterDialog({
  open,
  onClose,
  mode,
  setMode,
  driverIds,
  setDriverIds,
  activeMembers,
  leads,
  depotPoint,
  endPoint,
  onConfirm,
  running,
}: {
  open: boolean;
  onClose: () => void;
  mode: SplitMode;
  setMode: (m: SplitMode) => void;
  driverIds: Set<string>;
  setDriverIds: (s: Set<string>) => void;
  activeMembers: TeamMember[];
  leads: MappableLead[];
  depotPoint: { lat: number; lng: number } | null;
  endPoint: { lat: number; lng: number } | null;
  onConfirm: () => void;
  running: boolean;
}) {
  const drivers = useMemo(
    () =>
      activeMembers
        .filter((m) => driverIds.has(m.id))
        .map((m) => ({ id: m.id, name: m.fullName || m.email })),
    [activeMembers, driverIds],
  );

  // Re-run the splitter for the preview pane every time inputs change.
  // splitTerritory is pure + sub-millisecond so this is cheap.
  const preview = useMemo(() => {
    if (!depotPoint || leads.length === 0 || drivers.length < 2) return null;
    return splitTerritory({ mode, origin: depotPoint, end: endPoint, leads, drivers });
  }, [mode, depotPoint, endPoint, leads, drivers]);

  function toggleDriver(id: string) {
    const next = new Set(driverIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDriverIds(next);
  }

  const canConfirm = !running && drivers.length >= 2 && (preview?.assignments ?? []).some((a) => a.orderedLeadIds.length > 0);

  return (
    <Transition show={open}>
      <Dialog onClose={onClose} className="relative z-[1100]">
        <Transition.Child
          enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-xl rounded-xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
              <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <Compass className="h-5 w-5 text-brand-600" />
                Split across drivers
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-slate-500">
                {leads.length.toLocaleString()} leads will divide by {mode === "compass" ? "compass sector from the depot" : "ZIP code"}.
                Lead counts won't be balanced — admins choose how to balance by picking drivers.
              </Dialog.Description>

              {/* Mode toggle */}
              <div className="mt-4 inline-flex overflow-hidden rounded-md border border-slate-300 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("compass")}
                  className={mode === "compass" ? "bg-brand-600 px-3 py-1.5 font-semibold text-white" : "bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"}
                >
                  By compass (N/NE/E/SE/S/SW/W/NW)
                </button>
                <button
                  type="button"
                  onClick={() => setMode("zip")}
                  className={mode === "zip" ? "bg-brand-600 px-3 py-1.5 font-semibold text-white" : "bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"}
                >
                  By ZIP
                </button>
              </div>

              {/* Driver multi-select */}
              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Drivers ({drivers.length} picked)</h4>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-slate-200">
                  {activeMembers.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-500">No active team members.</p>
                  ) : (
                    activeMembers.map((m) => (
                      <label key={m.id} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={driverIds.has(m.id)}
                          onChange={() => toggleDriver(m.id)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                        />
                        <span className="flex-1 text-slate-700">{m.fullName || m.email}</span>
                        <span className="text-[10px] uppercase tracking-wide text-slate-400">
                          {m.role === "admin_user" ? "Admin" : m.role === "route_manager" ? "Mgr" : "Driver"}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Preview pane */}
              <div className="mt-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</h4>
                {!preview ? (
                  <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Pick at least 2 drivers + leads + a depot to see the split.
                  </p>
                ) : (
                  <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Driver</th>
                          <th className="px-3 py-1.5 text-left">{mode === "compass" ? "Sectors" : "ZIPs"}</th>
                          <th className="px-3 py-1.5 text-right">Stops</th>
                          <th className="px-3 py-1.5 text-right">Miles</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {preview.assignments.map((a) => (
                          <tr key={a.driverId} className={a.orderedLeadIds.length === 0 ? "opacity-50" : ""}>
                            <td className="px-3 py-1.5 font-medium text-slate-900">{a.driverName}</td>
                            <td className="px-3 py-1.5 text-slate-600">{a.groupLabels.join(", ") || "—"}</td>
                            <td className="px-3 py-1.5 text-right text-slate-700">{a.orderedLeadIds.length}</td>
                            <td className="px-3 py-1.5 text-right text-slate-500">{formatMiles(a.totalMeters)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {preview && preview.unassigned.length > 0 && (
                  <p className="mt-2 text-[11px] text-amber-700">
                    {preview.unassigned.length} lead(s) excluded — missing {mode === "zip" ? "ZIP code" : "coordinates"}.
                  </p>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  disabled={!canConfirm}
                  className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {running ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-4 w-4 animate-spin" /> Dispatching…
                    </span>
                  ) : (
                    `Dispatch ${(preview?.assignments ?? []).filter((a) => a.orderedLeadIds.length > 0).length} routes`
                  )}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

